import {
  AddRounded,
  AttachFileOutlined,
  ChevronRightRounded,
  CloseRounded,
  EastRounded,
} from "@mui/icons-material";
import { Box, Button, IconButton, Tooltip } from "@mui/material";

import { isEmpty, isNil, size } from "lodash";
import PropTypes from "prop-types";
import { lazy, Suspense, useCallback, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import useLocalStorageState from "use-local-storage-state";
import CheckboxInput from "../../../../../_src_shared/components/inputs/CheckboxInput";
import { columnTypesBoolean } from "../../../../../_src_shared/utils/agGrid/columnTypes/columnTypesBoolean";
import { columnTypesDate } from "../../../../../_src_shared/utils/agGrid/columnTypes/columnTypesDate";
import CellRendererUserByEmail from "../../../../../components/agGrid/cellRenderer/CellRendererUserByEmail";
import SearchInput from "../../../../../components/inputs/SearchInput";
import Flx from "../../../../../components/layout/Flx";
import LoadingFallback from "../../../../../components/LoadingFallback";
import Txt from "../../../../../components/typography/Txt";
import TitledCard from "../../../../../components/ui/TitledCard";
import { LOCAL_STORAGE_KEYS } from "../../../../../constants/keyNames/localStorage";
import { sidebarSetValues } from "../../../../../redux/actions/sidebarActions";
import LoanFileCommentsBadge from "../../../../../components/common/Loan/LoanFileCommentsBadge";

const AgGridReact = lazy(() =>
  import("ag-grid-react").then((module) => ({ default: module.AgGridReact }))
);
const AgGridTableWrapper = lazy(
  () => import("../../../../../components/agGrid/AgGridTableWrapper")
);

const LoanBulkFileUploader = lazy(
  () => import("../../../shared/LoanBulkFileUploader")
);
const LoanDocumentsTreeMap = lazy(
  () => import("../../../shared/LoanDocumentsTreeMap")
);

const SummaryTabLoanFilesCard = ({ baseUrl }) => {
  const loanDrilldown = useSelector((state) => state.loanDrilldown);
  const loanDocuments = useMemo(
    () => loanDrilldown?.loanDocuments,
    [loanDrilldown?.loanDocuments]
  );
  const [showTreeMap, setShowTreeMap] = useLocalStorageState(
    LOCAL_STORAGE_KEYS,
    {
      defaultValue: true,
    }
  );
  const navigate = useNavigate();
  const [showUpload, setShowUpload] = useState(false);
  const [quickFilter, setQuickFilter] = useState(null);

  const filesDataInit = useMemo(() => {
    return {
      docGroup: null,
      docType: null,
    };
  }, []);

  const [filesData, setFilesData] = useState(filesDataInit);

  const activeDocGroup = useMemo(() => filesData?.docGroup, [filesData]);
  const activeDocType = useMemo(() => filesData?.docType, [filesData]);

  const handleOnTreeMapClick = useCallback(
    (params) => {
      const group =
        filesData?.docGroup === params?.docGroup ? null : params?.docGroup;
      const type =
        filesData?.docType === params?.docType ? null : params?.docType;

      setFilesData({ docGroup: group, docType: type });
    },
    [filesData]
  );

  const handleOnDocGroupClick = useCallback((docGroup) => {
    setFilesData({ docGroup, docType: null });
  }, []);

  const handleClearFiles = useCallback(() => {
    setFilesData(filesDataInit);
  }, [filesDataInit]);

  const onShowTreeMapChange = useCallback(
    (v) => {
      if (!v) {
        handleClearFiles(); // Clear files when tree map is hidden
      }
      setShowTreeMap(v);
    },
    [handleClearFiles, setShowTreeMap]
  );

  return (
    <TitledCard
      title={`Uploaded Files (${size(loanDrilldown?.loanDocuments)})`}
      suppressPadding
      variant="h3"
      icon={<AttachFileOutlined />}
      suppressBodyPadding
      headerEndContent={
        <Flx ac gap={1} sx={{ position: "absolute", right: 10, top: 10 }}>
          {!isEmpty(loanDocuments) && (
            <CheckboxInput
              size="small"
              // labelFontSize="12px"
              sx={{ ".MuiCheckbox-root": { py: 0, px: 0.8 } }}
              label={"Show File Tree Chart"}
              checked={showTreeMap}
              onChange={onShowTreeMapChange}
            />
          )}
          <Tooltip title="Upload New Files" disableInteractive>
            <IconButton onClick={() => setShowUpload(true)}>
              <AddRounded className="thin" />
            </IconButton>
          </Tooltip>
        </Flx>
      }
      bodySx={{ pt: 0, px: 0 }}
    >
      {isEmpty(loanDocuments) ? (
        <Box sx={{ p: 3 }}>
          <Txt secondary>No files uploaded</Txt>
        </Box>
      ) : (
        <>
          {showTreeMap && (
            <FilesTreeMap
              loanDocuments={loanDocuments}
              onClick={handleOnTreeMapClick}
              activeDocGroup={activeDocGroup}
              activeDocType={activeDocType}
            />
          )}
          {!isEmpty(loanDocuments) && (
            <Flx fw jb ac sx={{ px: 2, pt: showTreeMap ? 0 : 2 }}>
              <SearchInput value={quickFilter} onChange={setQuickFilter} />
            </Flx>
          )}

          <LoanDocumentsTableWithNavigation
            loanDocuments={loanDocuments}
            activeDocGroup={activeDocGroup}
            onDocGroupClick={handleOnDocGroupClick}
            activeDocType={activeDocType}
            quickFilter={quickFilter}
            clearFiles={handleClearFiles}
          />

          <Suspense fallback={<LoadingFallback />}>
            <LoanBulkFileUploader
              show={showUpload}
              onClose={() => setShowUpload(false)}
            />
          </Suspense>
          <Flx end fw sx={{ p: 2 }}>
            <Button
              variant="text"
              onClick={() => navigate(`${baseUrl}/files`)}
              endIcon={<EastRounded className="thin" />}
            >
              More Details
            </Button>
          </Flx>
        </>
      )}
    </TitledCard>
  );
};

const LoanDocumentsTableWithNavigation = ({
  activeDocGroup,
  activeDocType,
  onDocGroupClick,
  loanDocuments,
  quickFilter,
  clearFiles,
}) => {
  if (isEmpty(loanDocuments)) {
    return null;
  }

  return (
    <>
      {!isNil(activeDocGroup) || !isNil(activeDocType) ? (
        <Flx fw jb ac sx={{ px: 2, pt: 2 }}>
          <Flx jb ac>
            <Button
              variant="text"
              onClick={() => onDocGroupClick(null)}
              sx={{
                fontWeight:
                  isNil(activeDocGroup) && isNil(activeDocType) ? 700 : 400,
              }}
            >
              Loan Files
            </Button>

            {!isNil(activeDocGroup) && (
              <>
                <ChevronRightRounded className="thin" />
                <Button
                  variant="text"
                  onClick={() => onDocGroupClick(activeDocGroup)}
                  sx={{ fontWeight: isNil(activeDocType) ? 700 : 400 }}
                >
                  {activeDocGroup}
                </Button>
              </>
            )}
            {!isNil(activeDocType) && (
              <>
                <ChevronRightRounded className="thin" />
                <Button variant="text">{activeDocType}</Button>
              </>
            )}
          </Flx>

          <IconButton onClick={clearFiles} size="small">
            <CloseRounded className="thin" />
          </IconButton>
        </Flx>
      ) : null}

      <Box px={1}>
        <LoanDocumentsTable
          loanDocuments={loanDocuments}
          activeDocGroup={activeDocGroup}
          quickFilter={quickFilter}
          activeDocType={activeDocType}
        />
      </Box>
    </>
  );
};

const LoanDocumentsTable = ({
  quickFilter,
  loanDocuments,
  activeDocGroup,
  activeDocType,
}) => {
  const ref = useRef();
  const dispatch = useDispatch();
  const rowData = useMemo(() => loanDocuments, [loanDocuments]);

  // Column definitions
  const columnDefs = useMemo(
    () => [
      {
        headerName: "Name",
        field: "file_display_name",
        flex: 1,
        cellRenderer: (params) => {
          return (
            <>
              {params.value}
              <LoanFileCommentsBadge file={params.data} />
            </>
          );
        },
      },
      { headerName: "Document Category", field: "docGroup", sort: "asc" },
      { headerName: "Document Type", field: "docType", sort: "asc" },
      { headerName: "Uploaded", field: "uploadDate", type: "dateTime" },
      {
        headerName: "Uploaded By",
        field: "uploadedBy",
        width: 160,
        wrapHeaderText: true,
        autoHeaderHeight: true,
        suppressHeaderFilterButton: true,
        suppressHeaderMenuButton: true,
        cellRenderer: CellRendererUserByEmail,
      },
    ],
    []
  );

  // Default column settings
  const defaultColDef = useMemo(
    () => ({
      // filter: true,
      // enableRowGroup: true,
      // enablePivot: true,
      // suppressHeaderFilterButton: true,
      // suppressHeaderMenuButton: true,
      // menuTabs: ["filterMenuTab"],
    }),
    []
  );

  // Custom column types
  const columnTypes = useMemo(
    () => ({
      ...columnTypesBoolean(),
      ...columnTypesDate(),
    }),
    []
  );

  // AG Grid external‐filter hooks
  const isExternalFilterPresent = useCallback(() => {
    return Boolean(activeDocGroup || activeDocType);
  }, [activeDocGroup, activeDocType]);

  const doesExternalFilterPass = useCallback(
    (node) => {
      const { docGroup, docType } = node.data;

      // Document Group filter
      if (activeDocGroup) {
        if (activeDocGroup === "No Category") {
          // match only when docGroup is null or undefined
          if (docGroup != null) {
            return false;
          }
        } else {
          if (docGroup !== activeDocGroup) {
            return false;
          }
        }
      }

      // Document Type filter
      if (activeDocType) {
        if (activeDocType === "No Category") {
          // match only when docType is null or undefined
          if (docType != null) {
            return false;
          }
        } else {
          if (docType !== activeDocType) {
            return false;
          }
        }
      }

      return true;
    },
    [activeDocGroup, activeDocType]
  );

  const onRowClicked = useCallback(
    (params) => {
      dispatch(
        sidebarSetValues({
          type: "filePreview",
          state: params.data,
        })
      );
    },
    [dispatch]
  );

  if (isEmpty(rowData)) {
    return null;
  }

  return (
    <Suspense fallback={<LoadingFallback />}>
      <AgGridTableWrapper
        // sx={{ borderTop: "1px solid #dddddd" }}
        simpleTable
        suppressMinHeightWhenPopulated={rowData}
      >
        <AgGridReact
          ref={ref}
          // onGridReady={onGridReady}
          rowData={rowData}
          columnDefs={columnDefs}
          quickFilterText={quickFilter}
          defaultColDef={defaultColDef}
          columnTypes={columnTypes}
          // wire up our external‐filter methods:
          isExternalFilterPresent={isExternalFilterPresent}
          doesExternalFilterPass={doesExternalFilterPass}
          onRowClicked={onRowClicked}
          allowDragFromColumnsToolPanel={false}
          domLayout="autoHeight"
          // enableCellTextSelection
        />
      </AgGridTableWrapper>
    </Suspense>
  );
};

const FilesTreeMap = ({
  loanDocuments,
  onClick,
  activeDocGroup,
  activeDocType,
}) => {
  return (
    <Suspense fallback={<LoadingFallback />}>
      <LoanDocumentsTreeMap
        loanDocuments={loanDocuments}
        onClick={onClick}
        activeDocGroup={activeDocGroup}
        activeDocType={activeDocType}
      />
    </Suspense>
  );
};

LoanDocumentsTableWithNavigation.propTypes = {
  activeDocGroup: PropTypes.string,
  activeDocType: PropTypes.string,
  onDocGroupClick: PropTypes.func.isRequired,
  loanDocuments: PropTypes.array,
  clearFiles: PropTypes.func.isRequired,
};

LoanDocumentsTable.propTypes = {
  quickFilter: PropTypes.string,
  loanDocuments: PropTypes.array,
  activeDocGroup: PropTypes.string,
  activeDocType: PropTypes.string,
};

FilesTreeMap.propTypes = {
  loanDocuments: PropTypes.array,
  onClick: PropTypes.func,
  activeDocGroup: PropTypes.string,
  activeDocType: PropTypes.string,
};

export default SummaryTabLoanFilesCard;
